import { isPlatformBrowser, Location } from '@angular/common';
import { inject, Inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import deDE from '../../../translations/de-DE.json';
import enUS from '../../../translations/en-US.json';
import { __ } from '../functions/object.functions';
import { BaseEnvironment, ENVIRONMENT } from '../tokens/environment.token';
import { LOCAL_STORAGE } from '../tokens/local-storage.token';
import { Logger } from './logger.service';

const log = new Logger('I18nService');

const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */

export function extract(s: string) {
  return s;
}

export class Language {
  locale: string;
  name: string;
  shortLocale: string;
}

@Injectable({ providedIn: 'root' })
export class I18nService {
  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  defaultLanguage: Language;

  supportedLanguages: Language[];

  localStorage: Storage | null = inject(LOCAL_STORAGE);

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private _language: Language;

  private request = Inject(REQUEST);

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private location: Location,
    @Inject(ENVIRONMENT) private environment: BaseEnvironment,
  ) {
    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('de-DE', deDE);
    translateService.addLangs(environment.supportedLanguages.map((l) => l.locale));
    translateService.setFallbackLang(environment.defaultLanguage.locale);
    this.defaultLanguage = environment.defaultLanguage;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */

  init(supportedLanguages: Language[]) {
    this.supportedLanguages = supportedLanguages;

    const path: string = isPlatformBrowser(this.platformId)
      ? window.location.pathname
      : this.location.path();
    this.setLanguageByPath(path);

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      if (isPlatformBrowser(this.platformId)) {
        this.localStorage?.setItem(languageKey, event.lang);
      }
    });
  }

  /**
   * Sets the language by url path.
   *
   * @param {string} path - The url path to set the language from.
   * @returns {void}
   */
  setLanguageByPath(path: string): void {
    if (path.startsWith('/de')) {
      this.language = 'de';
    } else if (path.startsWith('/en')) {
      this.language = 'en';
    } else {
      this.language = undefined;
    }
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: any) {
    language =
      language ||
      this.localStorage?.getItem(languageKey) ||
      this.translateService.getBrowserCultureLang();
    let foundLanguage = this.environment.supportedLanguages.find((q) => q.locale === language);

    // If no exact match is found, search without the region
    if (language && __.IsNullOrUndefined(foundLanguage)) {
      language = language.split('-')[0];

      foundLanguage = this.environment.supportedLanguages.find((supportedLanguage) =>
        supportedLanguage.locale.startsWith(language),
      );
    }

    // Fallback if language is not supported
    if (__.IsNullOrUndefined(foundLanguage)) {
      foundLanguage = __.IsNullOrUndefined(this.defaultLanguage) ? undefined : this.defaultLanguage;
    }

    this._language = foundLanguage!;

    this.translateService.use((this._language as Language).locale);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): any {
    let language = '';

    if (__.IsNullOrUndefined(this._language)) {
      this.language = undefined;
      language = this.defaultLanguage.locale;
    } else {
      language = __.IsNullOrUndefined((this._language as any).locale)
        ? this._language
        : (this._language as any).locale;
    }

    return language;
  }

  /**
   * Gets the current short locale.
   * @return {string|null} The current short locale code.
   */
  get shortLocaleOrNull(): string | null {
    if (!__.IsNullOrUndefined(this._language)) {
      const language = this.language;
      const shortLocale = this.environment.supportedLanguages.find((q) => q.locale === language);
      return shortLocale?.shortLocale ?? 'en';
    }

    return null;
  }

  /**
   * Gets the absolute URL with the current language.
   *
   * @param {string} url - The URL to get the absolute URL for.
   * @returns {string} - The absolute URL with the current language.
   */
  getAbsoluteUrlWithCurrentLanguage(url: string): string | null {
    if (__.IsNullOrUndefined(url)) {
      return null;
    }

    if (['admin', 'intranet'].includes(this.environment.applicationName)) {
      return `/${url}`;
    }

    const currentShortLocale = this.shortLocaleOrNull;
    let separator = '/';

    if (url.startsWith('/')) {
      separator = '';
    }

    if (
      [
        // 'specialist',
      ].some((q) => (url.startsWith('/') ? url.substring(1) : url).includes(q))
    ) {
      return `${separator}${url}`;
    }

    return `/${currentShortLocale}${separator}${url}`;
  }
}
